import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Loader from '@/components/Loader';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import InputSearch from '@/design_system/InputSearch';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import {
  ActionTypeFromGetMany,
  PackActionTypeFromGetMany,
  useActionTypes,
} from '@/models/actionType';
import { useCreateAction, useHasActionAlreadyBeenRefused } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import useDebouncedState from '@/utils/useDebouncedState';

import { ActionDropdownItem, CustomActionDropdownItem } from './ActionDropdownItem';
import { PackActionDropdownItem } from './PackActionDropdownItem';

type ActionTypeOption = ActionTypeFromGetMany | PackActionTypeFromGetMany;

export const ActionTypeSearchDialog = ({
  isOpen,
  onClose,
  onActionCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onActionCreated: (createdActionId: string) => void;
}) => {
  const { t } = useLingui();

  return (
    <Dialog
      title={t({ id: 'action-type-search-dialog.actions.add', message: 'Add an action' })}
      isOpen={isOpen}
      onOpenChange={onClose}
      fullHeight
    >
      <ActionTypeSearchDialogContent onActionCreated={onActionCreated} />
    </Dialog>
  );
};

const ActionTypeSearchDialogContent = ({
  onActionCreated,
}: {
  onActionCreated: (createdActionId: string) => void;
}) => {
  const { t } = useLingui();
  const { article, request, view } = useArticleContext();

  const [query, debouncedQuery, setQuery] = useDebouncedState<string>('', 500);
  const [hasClickedCustom, setHasClickedCustom] = useState(false);

  const {
    data: { actionTypes, packActionTypes } = { actionTypes: [], packActionTypes: [] },
    isFetching,
  } = useActionTypes(
    {
      articleId: article.id,
      query: debouncedQuery || undefined,
    },
    {
      keepPreviousData: true,
    }
  );

  const actionTypeOptions = [...packActionTypes, ...actionTypes];

  const {
    mutateAsync: createAction,
    isPending: isPendingCreateAction,
    isSuccess: isSuccessCreateAction,
  } = useCreateAction({
    requestId: request.id,
    articleId: article.id,
  });

  const handleCreateAction = async (
    action: ActionTypeFromGetMany | PackActionTypeFromGetMany | 'custom'
  ) => {
    const response = await createAction(
      action === 'custom'
        ? {
            description: '',
          }
        : 'actions' in action
          ? {
              packActionTypeOrganization: action,
            }
          : {
              actionTypeOrganization: action,
            }
    );

    if (!response) {
      return;
    }

    onActionCreated(response.id);
  };

  const isPending = isPendingCreateAction || isSuccessCreateAction;

  return (
    <>
      <main>
        <Stack gap="0.5rem">
          <InputSearch
            aria-label={t({
              id: 'action-type-search-dialog.add-action.label',
              message: 'Add an action',
            })}
            placeholder={t({
              id: 'action-type-search-dialog.actions.placeholder',
              message: 'Search a care and repair action...',
            })}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            isLoading={isFetching || query !== debouncedQuery}
            value={query}
            onChange={setQuery}
          />

          {actionTypeOptions.length === 0 && (
            <InlineText color="secondary" style={{ padding: '0.5rem 0' }}>
              <Trans id="action-type-search-dialog.actions.no-results">
                No results matching your search
              </Trans>
            </InlineText>
          )}

          <ActionTypesMenu
            actionTypeOptions={actionTypeOptions}
            onChooseActionType={handleCreateAction}
            isPending={isPending}
          />
        </Stack>
      </main>

      {view.services.actions.customEnabled && (
        <Footer>
          <FooterButtons>
            <Button
              variant="secondary"
              onPress={() => {
                handleCreateAction('custom');
                setHasClickedCustom(true);
              }}
              disabled={isPending && !hasClickedCustom}
              isLoading={isPending && hasClickedCustom}
            >
              <CustomActionDropdownItem style={{ display: 'flex', justifyContent: 'center' }} />
            </Button>
          </FooterButtons>
        </Footer>
      )}
    </>
  );
};

const ActionTypesMenu = ({
  actionTypeOptions,
  onChooseActionType,
  isPending,
}: {
  actionTypeOptions: ActionTypeOption[];
  onChooseActionType: (actionType: ActionTypeOption) => void;
  isPending: boolean;
}) => {
  return (
    <Stack>
      {actionTypeOptions.map((actionTypeOption) => (
        <ActionTypeMenuItem
          key={actionTypeOption.id}
          actionTypeOption={actionTypeOption}
          onChooseActionType={onChooseActionType}
          isPending={isPending}
        />
      ))}
    </Stack>
  );
};

const ActionTypeMenuItem = ({
  actionTypeOption,
  onChooseActionType,
  isPending,
}: {
  actionTypeOption: ActionTypeOption;
  onChooseActionType: (actionType: ActionTypeOption) => void;
  isPending: boolean;
}) => {
  const { article } = useArticleContext();
  const hasActionAlreadyBeenRefused = useHasActionAlreadyBeenRefused(article.id);
  const [hasClicked, setHasClicked] = useState(false);

  return (
    <Button
      style={{ padding: '0.5rem' }}
      variant="style-less"
      onPress={() => {
        onChooseActionType(actionTypeOption);
        setHasClicked(true);
      }}
    >
      <Stack row gap="0.25rem" style={{ flex: 1 }} alignItems="center">
        {'actions' in actionTypeOption ? (
          <PackActionDropdownItem
            packAction={actionTypeOption}
            refusedByClient={hasActionAlreadyBeenRefused(actionTypeOption.id)}
          />
        ) : (
          <ActionDropdownItem
            action={actionTypeOption}
            refusedByClient={hasActionAlreadyBeenRefused(actionTypeOption.id)}
          />
        )}
        {isPending && hasClicked && <Loader />}
      </Stack>
    </Button>
  );
};
